import { useState, useEffect, useContext, useRef, useMemo, Fragment } from "react";
import { GoogleMap, Marker, InfoWindow, OverlayView, OverlayViewF, useLoadScript } from "@react-google-maps/api";
import secureLocalStorage from "react-secure-storage";
import { MyContext } from "../Context/MyProvider";
import PickupPin from "../assets/Image/pickup.png";
import DropPin from "../assets/Image/drop.png";

const getVehicleSize = (imageUrl) => {
    const height = 55; // Standard professional height on the map
    let ratio = 0.62; // Default aspect ratio (width / height)
    
    if (imageUrl) {
        const urlLower = imageUrl.toLowerCase();
        if (urlLower.includes('download1') || urlLower.includes('motorbike')) {
            ratio = 0.575;
        } else if (urlLower.includes('auto-rickshaw') || urlLower.includes('riksha')) {
            ratio = 0.639;
        } else if (urlLower.includes('group1000004645') || urlLower.includes('taxi')) {
            ratio = 0.618;
        } else if (urlLower.includes('group1000004644') || urlLower.includes('hoverboard') || urlLower.includes('hovrbord')) {
            ratio = 0.788;
        } else if (urlLower.includes('group1000004647') || urlLower.includes('group1000004646') || urlLower.includes('sedan') || urlLower.includes('car.png') || urlLower.includes('cars')) {
            ratio = 0.667;
        } else if (urlLower.includes('jeep')) {
            ratio = 0.567;
        } else if (urlLower.includes('shipping-truck')) {
            ratio = 0.558;
        } else if (urlLower.includes('van.png')) {
            ratio = 0.565;
        } else if (urlLower.includes('truck.png')) {
            ratio = 0.482;
        }
    }
    const width = Math.round(height * ratio);
    return new window.google.maps.Size(width, height);
};

const silverMapStyle = [
  {
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#f1f1f1"
      }
    ]
  },
  {
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#5f6368"
      }
    ]
  },
  {
    "elementType": "labels.text.stroke",
    "stylers": [
      {
        "color": "#ffffff"
      }
    ]
  },
  {
    "featureType": "administrative.locality",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#B2BEB5"
      }
    ]
  },
  {
    "featureType": "poi",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#B2BEB5"
      }
    ]
  },
  {
    "featureType": "poi.park",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#e0e0e0"
      }
    ]
  },
  {
    "featureType": "poi.park",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#6b9a76"
      }
    ]
  },
  {
    "featureType": "road",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#ffffff"
      }
    ]
  },
  {
    "featureType": "road",
    "elementType": "geometry.stroke",
    "stylers": [
      {
        "color": "#dcdcdc"
      }
    ]
  },
  {
    "featureType": "road",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#9ca5b3"
      }
    ]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#e1e1e1"
      }
    ]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry.stroke",
    "stylers": [
      {
        "color": "#b3b3b3"
      }
    ]
  },
  {
    "featureType": "road.highway",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#7d7d7d"
      }
    ]
  },
  {
    "featureType": "transit",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#f2f2f2"
      }
    ]
  },
  {
    "featureType": "transit.station",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#B2BEB5"
      }
    ]
  },
  {
    "featureType": "water",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#c8d3d6"
      }
    ]
  },
  {
    "featureType": "water",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#5b6b74"
      }
    ]
  },
  {
    "featureType": "water",
    "elementType": "labels.text.stroke",
    "stylers": [
      {
        "color": "#ffffff"
      }
    ]
  }
];

const Map = ({
    pickupLocation,
    dropLocation,
    stopList = [],
    step,
    HandleGetGoogleAddress,
    categoryData = [],
}) => {
    
    const { BaseUrl } = useContext(MyContext);

    const [map, setMap] = useState(null);
    const [coords, setCoords] = useState(null);

    const renderersRef = useRef([]);
    const fallbackPolylinesRef = useRef([]);
    const animationIntervalsRef = useRef([]);

    const ShowCurrentMarker = step === 2 && coords;
    const ShowPickupMarker = pickupLocation && step !== 2;
    const ShowDropMarker = dropLocation && step !== 2;
    const ShowStopListMarker = step !== 2;
    const ShowCatListMarker = step === 3;

    const pickupMinutes = useMemo(() => {
        if (!pickupLocation || !categoryData || categoryData.length === 0) {
            return "2 min";
        }

        const getLatLngDistance = (lat1, lng1, lat2, lng2) => {
            const R = 6371; // Radius of the earth in km
            const dLat = (lat2 - lat1) * Math.PI / 180;
            const dLon = (lng2 - lng1) * Math.PI / 180;
            const a = 
                Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
                Math.sin(dLon/2) * Math.sin(dLon/2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
            return R * c; // Distance in km
        };

        let minDistance = Infinity;
        categoryData.forEach(driver => {
            if (driver.latitude && driver.longitude) {
                const dist = getLatLngDistance(
                    Number(pickupLocation.lat),
                    Number(pickupLocation.lng),
                    Number(driver.latitude),
                    Number(driver.longitude)
                );
                if (dist < minDistance) {
                    minDistance = dist;
                }
            }
        });

        if (minDistance === Infinity) {
            return "2 min";
        }

        // Estimate time: ~2.5 minutes per km, minimum 1 min
        const estimatedTime = Math.max(1, Math.round(minDistance * 2.5));
        return `${estimatedTime} min`;
    }, [pickupLocation, categoryData]);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: secureLocalStorage.getItem("mapkey"),
    });

    useEffect(() => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition((position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            setCoords({ lat, lng });
        });
    }, []);

    useEffect(() => {
        updateMapCenter();
    }, [pickupLocation, dropLocation, stopList, coords, step]);

    useEffect(() => {
        if (step === 2 && coords) {
            HandleGetGoogleAddress(coords.lat, coords.lng);
        }
    }, [step]);

    const updateMapCenter = () => {
        if (!map) return;

        // Step 2: just pan to current coords
        if (step === 2 && coords) {
            map.panTo(coords);
            return;
        }

        // Only adjust bounds if **both pickup and drop exist**
        if (pickupLocation && dropLocation) {
            const bounds = new window.google.maps.LatLngBounds();

            // Add pickup and drop points
            bounds.extend({
                lat: Number(pickupLocation.lat),
                lng: Number(pickupLocation.lng),
            });

            bounds.extend({
                lat: Number(dropLocation.lat),
                lng: Number(dropLocation.lng),
            });

            // Optional: include stops if any
            stopList.forEach((stop) => {
                if (stop) {
                    bounds.extend({
                        lat: Number(stop.lat),
                        lng: Number(stop.lng),
                    });
                }
            });

            map.fitBounds(bounds); // automatically zooms and centers
            return;
        }

        // If only pickup exists
        if (pickupLocation) {
            map.panTo({
                lat: Number(pickupLocation.lat),
                lng: Number(pickupLocation.lng),
            });
            return;
        }

        // If only drop exists
        if (dropLocation) {
            map.panTo({
                lat: Number(dropLocation.lat),
                lng: Number(dropLocation.lng),
            });
            return;
        }

        // Fallback: pan to current location
        if (coords) {
            map.panTo(coords);
        }
    };

    const handleMapClick = (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        setCoords({ lat, lng });
        HandleGetGoogleAddress(lat, lng);
    };

    const routePoints = useMemo(() => {
        const points = [];

        if (pickupLocation)
            points.push({ lat: Number(pickupLocation.lat), lng: Number(pickupLocation.lng) });

        stopList.forEach((stop) => {
            if (stop) points.push({ lat: Number(stop.lat), lng: Number(stop.lng) });
        });

        if (dropLocation)
            points.push({ lat: Number(dropLocation.lat), lng: Number(dropLocation.lng) });

        return points;

    }, [pickupLocation, dropLocation, stopList]);

    const clearRoutes = () => {
        renderersRef.current.forEach((renderer) => renderer.setMap(null));
        renderersRef.current = [];

        fallbackPolylinesRef.current.forEach((polyline) => polyline.setMap(null));
        fallbackPolylinesRef.current = [];

        animationIntervalsRef.current.forEach((intervalId) => clearInterval(intervalId));
        animationIntervalsRef.current = [];
    };

    const drawFallbackArc = (start, end) => {
        const lat1 = typeof start.lat === 'function' ? start.lat() : start.lat;
        const lng1 = typeof start.lng === 'function' ? start.lng() : start.lng;
        const lat2 = typeof end.lat === 'function' ? end.lat() : end.lat;
        const lng2 = typeof end.lng === 'function' ? end.lng() : end.lng;

        const numPoints = 80;
        const arcPoints = [];
        
        const deltaLat = lat2 - lat1;
        const deltaLng = lng2 - lng1;
        
        // Curved offset factor (arcs slightly upwards)
        const curvature = 0.06;

        for (let i = 0; i <= numPoints; i++) {
            const t = i / numPoints;
            
            // Linear interpolation
            let lat = lat1 + deltaLat * t;
            let lng = lng1 + deltaLng * t;
            
            // Perpendicular sine wave offset
            const offsetFraction = Math.sin(t * Math.PI);
            lat += (-deltaLng) * curvature * offsetFraction;
            lng += deltaLat * curvature * offsetFraction;
            
            arcPoints.push(new window.google.maps.LatLng(lat, lng));
        }

        // 1. Light background static connection line
        const bgPolyline = new window.google.maps.Polyline({
            path: arcPoints,
            strokeColor: "#4038ff",
            strokeOpacity: 0.18,
            strokeWeight: 4,
            map: map,
        });
        fallbackPolylinesRef.current.push(bgPolyline);

        // 2. Animated dash overlay
        const lineSymbol = {
            path: "M 0,-1 0,1",
            strokeOpacity: 1,
            scale: 3,
            strokeColor: "#4038ff",
            strokeWeight: 4
        };

        const animatedPolyline = new window.google.maps.Polyline({
            path: arcPoints,
            strokeColor: "#4038ff",
            strokeOpacity: 0,
            icons: [
                {
                    icon: lineSymbol,
                    offset: "0%",
                    repeat: "16px",
                },
            ],
            map: map,
        });
        fallbackPolylinesRef.current.push(animatedPolyline);

        // 3. Move the dashes along the path
        let count = 0;
        const intervalId = setInterval(() => {
            count = (count + 1) % 160;
            const offset = (count / 1.6) + "%";
            animatedPolyline.set("icons", [
                {
                    icon: lineSymbol,
                    offset: offset,
                    repeat: "16px",
                },
            ]);
        }, 22);
        animationIntervalsRef.current.push(intervalId);
    };

    const drawRoutes = () => {

        if (!map || routePoints.length < 2 || step === 2) {
            clearRoutes();
            return;
        }

        clearRoutes();

        const directionsService = new window.google.maps.DirectionsService();

        for (let i = 0; i < routePoints.length - 1; i++) {
            const renderer = new window.google.maps.DirectionsRenderer({
                map,
                suppressMarkers: true,
                preserveViewport: true,
                polylineOptions: {
                    strokeColor: "#4038ff",
                    strokeWeight: 5,
                },
            });

            directionsService.route(
                {
                    origin: routePoints[i],
                    destination: routePoints[i + 1],
                    travelMode: window.google.maps.TravelMode.DRIVING,
                },
                (result, status) => {
                    if (status === "OK") {
                        renderer.setDirections(result);
                        renderersRef.current.push(renderer);
                    } else {
                        console.warn("Google Directions Service failed:", status, ". Drawing animated arc fallback.");
                        drawFallbackArc(routePoints[i], routePoints[i + 1]);
                    }
                }
            );
        }
    };

    const routePointsStr = JSON.stringify(routePoints);

    useEffect(() => {
        if (step === 2) {
            clearRoutes();
        } else {
            drawRoutes();
        }
    }, [map, routePointsStr, step]);


    if (!isLoaded) {
        return <div className="w-full h-full shimmer"></div>;
    }

    const pickupSymbol = {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: "#22c55e",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
        scale: 7,
    };

    const stopSymbol = {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: "#eab308",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
        scale: 7,
    };

    const dropSymbol = {
        path: "M -6 -6 H 6 V 6 H -6 Z",
        fillColor: "#ef4444",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
        scale: 1,
    };

    return (
        <div className="google_map">
            <GoogleMap
                center={coords}
                zoom={14}
                onLoad={(mapInstance) => setMap(mapInstance)}
                onClick={step === 2 && handleMapClick}
                options={{
                    mapTypeControl: false,
                    fullscreenControl: false,
                    streetViewControl: false,
                    zoomControl: true,
                    styles: silverMapStyle,
                }}
            >

                {ShowCurrentMarker && (
                    <Marker
                        position={coords}
                        icon={pickupSymbol}
                    />
                )}

                {/* Pickup Marker */}
                {ShowPickupMarker && (
                    <>
                        <Marker
                            position={{
                                lat: Number(pickupLocation.lat),
                                lng: Number(pickupLocation.lng),
                            }}
                            icon={pickupSymbol}
                        />
                        <OverlayViewF
                            position={{
                                lat: Number(pickupLocation.lat),
                                lng: Number(pickupLocation.lng),
                            }}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                            <div style={{
                                position: 'absolute',
                                transform: 'translate(-50%, calc(-100% - 10px))',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                whiteSpace: 'nowrap'
                            }}>
                                <div className="flex items-center bg-white shadow-xl rounded-lg overflow-hidden border border-gray-100 font-sans">
                                    <div className="text-white px-2.5 py-1.5 font-bold text-[10px] flex items-center justify-center min-w-[36px] tracking-wider uppercase bg-[#4038ff]">
                                        {pickupMinutes}
                                    </div>
                                    <div className="px-3 py-1.5 text-gray-800 text-[11px] font-semibold flex items-center">
                                        <span>{`From ${pickupLocation.title}`}</span>
                                    </div>
                                </div>
                                <div className="w-0 h-0" style={{ borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid white', marginTop: '-1px' }} />
                            </div>
                        </OverlayViewF>
                    </>
                )}

                {/* Drop Marker */}
                {ShowDropMarker && (
                    <>
                        <Marker
                            position={{
                                lat: Number(dropLocation.lat),
                                lng: Number(dropLocation.lng),
                            }}
                            icon={dropSymbol}
                        />
                        <OverlayViewF
                            position={{
                                lat: Number(dropLocation.lat),
                                lng: Number(dropLocation.lng),
                            }}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                            <div style={{
                                position: 'absolute',
                                transform: 'translate(-50%, calc(-100% - 10px))',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                whiteSpace: 'nowrap'
                            }}>
                                <div className="flex items-center bg-white shadow-xl rounded-lg overflow-hidden border border-gray-100 font-sans">
                                    <div className="px-3 py-1.5 text-gray-800 text-[11px] font-semibold flex items-center">
                                        <span>{`To ${dropLocation.title}`}</span>
                                    </div>
                                </div>
                                <div className="w-0 h-0" style={{ borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid white', marginTop: '-1px' }} />
                            </div>
                        </OverlayViewF>
                    </>
                )}

                {/* Stops */}
                {ShowStopListMarker &&
                    stopList.map((item, index) => {
                        if (!item) return null;
                        return (
                            <Fragment key={index}>
                                <Marker
                                    position={{
                                        lat: Number(item.lat),
                                        lng: Number(item.lng),
                                    }}
                                    icon={stopSymbol}
                                />
                                <OverlayViewF
                                    position={{
                                        lat: Number(item.lat),
                                        lng: Number(item.lng),
                                    }}
                                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                >
                                    <div style={{
                                        position: 'absolute',
                                        transform: 'translate(-50%, calc(-100% - 10px))',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <div className="flex items-center bg-white shadow-xl rounded-lg overflow-hidden border border-gray-100 font-sans">
                                            <div className="px-3 py-1.5 text-gray-800 text-[11px] font-semibold flex items-center">
                                                <span>{`Stop ${index + 1}`}</span>
                                            </div>
                                        </div>
                                        <div className="w-0 h-0" style={{ borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid white', marginTop: '-1px' }} />
                                    </div>
                                </OverlayViewF>
                            </Fragment>
                        );
                    })}

                {/* Category Markers */}
                {ShowCatListMarker &&
                    categoryData.map((item, index) => (
                        <Marker
                            key={index}
                            icon={{
                                url: BaseUrl + item.image,
                                scaledSize: getVehicleSize(item.image),
                            }}
                            position={{
                                lat: Number(item.latitude),
                                lng: Number(item.longitude),
                            }}
                        />
                    ))}

            </GoogleMap>
        </div>
    );
};

export default Map;